import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Textarea,
  Heading,
  Text,
  HStack,
  Icon,
  useToast,
  VStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
} from "@chakra-ui/react";
import { FaStar } from "react-icons/fa";
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { firestore } from "../firebase/firebaseConfig";
import { useAuth } from "../context/AuthProvider";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";


const FeedbackForm = ({ tradeId, chatId, role, buyerId, sellerId, onComplete = () => {} }) => {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();

  const toast = useToast();
  const navigate = useNavigate();

  const buyerCriteria = ["responsiveness", "fairness", "transactionSpeed", "overallExperience"];
  const sellerCriteria = ["paymentTimeliness", "communication", "overallExperience"];
  const criteriaList = role === "buyer" ? buyerCriteria : sellerCriteria;

  const [feedback, setFeedback] = useState(
    criteriaList.reduce(
      (acc, criterion) => {
        acc[criterion] = 0;
        return acc;
      },
      { comment: "" }
    )
  );

  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const checkFeedbackStatus = async () => {
      console.log("Checking feedback status for trade:", tradeId);
      try {
        const feedbackDocRef = doc(firestore, "feedback", tradeId);
        const feedbackDoc = await getDoc(feedbackDocRef);

        if (feedbackDoc.exists()) {
          const feedbackData = feedbackDoc.data();
          const feedbackType = role === "buyer" ? "buyerFeedback" : "sellerFeedback";

          if (feedbackData.feedbacks?.[feedbackType]) {
            console.log(`Feedback already submitted for role: ${role}`);
            setHasSubmitted(true);
          }
        }
      } catch (error) {
        console.error("Error checking feedback status:", error);
      }
    };

    checkFeedbackStatus();
  }, [tradeId, role]);

  const handleStarClick = (key, value) => {
    console.log(`Rating updated for ${key}: ${value}`);
    setFeedback((prev) => ({ ...prev, [key]: value }));
  };

  const validateFeedback = () => {
    const hasMinimumStars = criteriaList.some((criterion) => feedback[criterion] > 0);
    if (!hasMinimumStars) {
      toast({
        title: t("feedbackform.incomplete_feedback_title"),
        description: t("feedbackform.incomplete_feedback_desc"),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return false;
    }
    return true;
  };

  const handleSubmitFeedback = async () => {
    if (!validateFeedback()) {
      return;
    }

    if (!user) {
      toast({
        title: t("feedbackform.unauthorized"),
        description: t("feedbackform.unauthorized_desc"),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const feedbackType = role === "buyer" ? "buyerFeedback" : "sellerFeedback";
    console.log("Submitting feedback:", feedback, "Role:", role);

    try {
      const feedbackDocRef = doc(firestore, "feedback", tradeId);
      const existingFeedbackDoc = await getDoc(feedbackDocRef);

      let feedbackData = {};
      if (existingFeedbackDoc.exists()) {
        feedbackData = existingFeedbackDoc.data();
        feedbackData.feedbacks[feedbackType] = {
          ...feedback,
          createdAt: serverTimestamp(),
          status: "Completed",
        };
      } else {
        feedbackData = {
          tradeId,
          buyerId,
          sellerId,
          feedbacks: {
            [feedbackType]: {
              ...feedback,
              createdAt: serverTimestamp(),
              status: "Completed",
            },
          },
          isFeedbackComplete: false,
        };
      }

      console.log("Feedback data before saving:", feedbackData);

      await setDoc(feedbackDocRef, feedbackData, { merge: true });

      const isBuyerFeedbackComplete = !!feedbackData.feedbacks.buyerFeedback;
      const isSellerFeedbackComplete = !!feedbackData.feedbacks.sellerFeedback;

      if (isBuyerFeedbackComplete && isSellerFeedbackComplete) {
        console.log("Both feedbacks complete. Marking feedback as completed.");
        await updateDoc(feedbackDocRef, { isFeedbackComplete: true });
        onComplete(); // Notify parent
      }

      console.log("Feedback successfully submitted.");
      setHasSubmitted(true);
      setIsModalOpen(true); // Open modal on successful submission
      toast({
        title: t("feedbackform.feedback_submitted_title"),
        description: t("feedbackform.feedback_submitted_desc"),
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error submitting feedback:", error);
      toast({
        title: t("feedbackform.error_title"),
        description: t("feedbackform.error_desc"),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleCloseModal = () => {
    console.log("Closing feedback modal.");
    setIsModalOpen(false);
    navigate(`/personal/chat/${chatId}`);
  };

  return (
    <>
      <Modal isOpen={isModalOpen} onClose={handleCloseModal} isCentered>
        <ModalOverlay />
        <ModalContent>
        <ModalHeader>{t("feedbackform.modal_title")}</ModalHeader>
        <ModalCloseButton />
          <ModalBody>
          <Text>{t("feedbackform.modal_desc")}</Text>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={handleCloseModal}>
            {t("feedbackform.close")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      {!hasSubmitted && (
        <Box bg="gray.50" p={4} borderRadius="lg" shadow="md">
          <Heading size="md" mb={4}>
          {t("feedbackform.leave_feedback")}
          </Heading>
          <VStack spacing={4} align="start">
            {criteriaList.map((criterion) => (
              <Box key={criterion}>
                <Text fontWeight="bold" mb={2}>{criterion.replace(/([A-Z])/g, " $1")}</Text>
                <HStack spacing={2}>
                  {[...Array(5)].map((_, i) => (
                    <Icon
                      key={i}
                      as={FaStar}
                      boxSize={6}
                      color={i < feedback[criterion] ? "yellow.400" : "gray.300"}
                      cursor="pointer"
                      onClick={() => handleStarClick(criterion, i + 1)}
                    />
                  ))}
                </HStack>
              </Box>
            ))}
            <Textarea
              placeholder={t("feedbackform.comment_placeholder")}
              value={feedback.comment}
              onChange={(e) => setFeedback({ ...feedback, comment: e.target.value })}
            />
          </VStack>

          <Button colorScheme="blue" onClick={handleSubmitFeedback} mt={4}>
          {t("feedbackform.submit_feedback")}
          </Button>
        </Box>
      )}
    </>
  );
};

export default FeedbackForm;
